import { useState, useMemo, useCallback, useEffect } from "react"
import { ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react"
import { cn } from "@/lib/utils"
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table"
import { AdminLoadingSkeleton } from "./admin-loading-skeleton"
import { AdminEmptyState } from "./admin-empty-state"
import { AdminPagination } from "./admin-pagination"

// ── Types ──

export interface AdminTableColumn {
  key: string
  label: string
  hideBelow?: "sm" | "md" | "lg" | "xl"
  width?: string
  sortable?: boolean
  sortKey?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sortFn?: (a: any, b: any) => number
}

export type SortDir = "asc" | "desc"

export interface SortState {
  key: string
  dir: SortDir
}

interface AdminTableProps<T> {
  columns: AdminTableColumn[]
  data: T[]
  loading?: boolean
  emptyIcon?: React.ReactNode
  emptyTitle?: string
  emptyActionHref?: string
  emptyActionLabel?: string
  renderCell: (item: T, column: AdminTableColumn) => React.ReactNode
  onRowClick?: (item: T) => void
  skeletonRows?: number
  pageSize?: number
  defaultSort?: SortState
  getRowId?: (item: T, index: number) => string
}

// ── Helpers ──

function responsiveClass(col: AdminTableColumn) {
  if (!col.hideBelow) return ""
  return `hidden ${col.hideBelow}:table-cell`
}

function compareValues(a: unknown, b: unknown, dir: SortDir): number {
  if (a == null && b == null) return 0
  if (a == null) return 1
  if (b == null) return -1

  const mult = dir === "asc" ? 1 : -1

  if (typeof a === "number" && typeof b === "number") {
    return (a - b) * mult
  }

  return String(a).localeCompare(String(b), "zh-Hant") * mult
}

function getNestedValue(obj: unknown, path: string): unknown {
  return path.split(".").reduce<unknown>((acc, part) => {
    if (acc && typeof acc === "object") return (acc as Record<string, unknown>)[part]
    return undefined
  }, obj)
}

// ── Component ──

function AdminTableInner<T>({
  columns,
  data,
  loading,
  emptyIcon,
  emptyTitle = "尚無資料",
  emptyActionHref,
  emptyActionLabel,
  renderCell,
  onRowClick,
  skeletonRows = 5,
  pageSize = 0,
  defaultSort,
  getRowId,
}: AdminTableProps<T>) {
  const [sort, setSort] = useState<SortState | null>(defaultSort ?? null)
  const [page, setPage] = useState(1)

  const dataLen = data.length
  useEffect(() => { setPage(1) }, [dataLen, pageSize])

  const handleSort = useCallback((col: AdminTableColumn) => {
    if (!col.sortable) return
    setSort((prev) => {
      const colKey = col.sortKey || col.key
      if (!prev || prev.key !== colKey) return { key: colKey, dir: "asc" }
      if (prev.dir === "asc") return { key: colKey, dir: "desc" }
      return null
    })
    setPage(1)
  }, [])

  const sortedData = useMemo(() => {
    if (!sort) return data
    const col = columns.find((c) => (c.sortKey || c.key) === sort.key)
    const arr = [...data]
    if (col?.sortFn) {
      const mult = sort.dir === "asc" ? 1 : -1
      arr.sort((a, b) => col.sortFn!(a, b) * mult)
    } else {
      arr.sort((a, b) => compareValues(
        getNestedValue(a, sort.key),
        getNestedValue(b, sort.key),
        sort.dir,
      ))
    }
    return arr
  }, [data, sort, columns])

  const paginationEnabled = pageSize > 0 && sortedData.length > pageSize
  const totalPages = pageSize > 0 ? Math.ceil(sortedData.length / pageSize) : 1
  const pagedData = paginationEnabled
    ? sortedData.slice((page - 1) * pageSize, page * pageSize)
    : sortedData

  if (loading) {
    return <AdminLoadingSkeleton rows={skeletonRows} />
  }

  if (data.length === 0) {
    return (
      <AdminEmptyState
        icon={emptyIcon}
        title={emptyTitle}
        actionHref={emptyActionHref}
        actionLabel={emptyActionLabel}
      />
    )
  }

  function renderSortIcon(col: AdminTableColumn) {
    if (!col.sortable) return null
    const colKey = col.sortKey || col.key
    if (!sort || sort.key !== colKey) {
      return <ArrowUpDown className="inline h-3 w-3 ml-1 opacity-40" />
    }
    return sort.dir === "asc"
      ? <ArrowUp className="inline h-3 w-3 ml-1" />
      : <ArrowDown className="inline h-3 w-3 ml-1" />
  }

  return (
    <div className="overflow-hidden rounded-xl border bg-card">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/50">
            {columns.map((col) => (
              <TableHead
                key={col.key}
                className={cn(
                  "text-xs font-medium uppercase tracking-wider",
                  responsiveClass(col),
                  col.width,
                  col.sortable && "cursor-pointer select-none hover:text-foreground transition-colors",
                )}
                onClick={col.sortable ? () => handleSort(col) : undefined}
              >
                {col.label}
                {renderSortIcon(col)}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {pagedData.map((item, idx) => (
            <TableRow
              key={getRowId ? getRowId(item, idx) : idx}
              className={cn(onRowClick && "cursor-pointer")}
              onClick={onRowClick ? () => onRowClick(item) : undefined}
            >
              {columns.map((col) => (
                <TableCell
                  key={col.key}
                  className={cn(responsiveClass(col), col.width)}
                >
                  {renderCell(item, col)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {paginationEnabled && (
        <AdminPagination
          page={page}
          totalPages={totalPages}
          totalItems={sortedData.length}
          pageSize={pageSize}
          onPrev={() => setPage((p) => Math.max(1, p - 1))}
          onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
          onJumpTo={(p) => setPage(Math.max(1, Math.min(totalPages, p)))}
        />
      )}
    </div>
  )
}

export const AdminTable = AdminTableInner
export const AdminDataTable = AdminTableInner
